import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill } from "./MerchantOverview";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function Chargebacks() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [evidence, setEvidence] = useState("");

  const load = () => api.get("/chargebacks").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const update = async (status) => {
    try {
      await api.post(`/chargebacks/${active.chargeback_id}/update`, { status, evidence: evidence || undefined });
      toast.success(`Marked as ${status.replace("_", " ")}`);
      setActive(null); setEvidence(""); load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="chargebacks-page">
      <p className="text-xs uppercase tracking-[0.18em] text-[#FF4757]">Disputes</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2">Chargebacks</h1>
      <p className="text-slate-500 mt-2 max-w-xl">
        Track open disputes, submit evidence, and view guarantee eligibility.
      </p>

      <div className="sk-card overflow-hidden mt-8">
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
          <div className="col-span-3">ID</div>
          <div className="col-span-3">Reason</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Guarantee</div>
          <div className="col-span-2 text-right">Disputed</div>
        </div>
        {rows.length === 0 && <div className="py-16 text-center text-slate-400 text-sm" data-testid="cb-empty">No chargebacks.</div>}
        {rows.map((cb) => (
          <button key={cb.chargeback_id} onClick={() => { setActive(cb); setEvidence(cb.evidence || ""); }}
            className="w-full text-left grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-slate-100 hover:bg-slate-50"
            data-testid={`cb-row-${cb.chargeback_id}`}>
            <div className="col-span-3 font-mono text-xs text-slate-500 truncate">{cb.chargeback_id}</div>
            <div className="col-span-3 truncate">{cb.reason}</div>
            <div className="col-span-2"><StatusPill status={cb.status} /></div>
            <div className="col-span-2">
              {cb.guarantee_eligible ? (
                <span className="inline-flex items-center gap-1 text-xs text-[#10B981]">
                  <ShieldCheck className="w-3 h-3" /> Eligible
                </span>
              ) : <span className="text-xs text-slate-400">Not eligible</span>}
            </div>
            <div className="col-span-2 text-right font-mono">${Number(cb.amount_disputed).toFixed(2)}</div>
          </button>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="bg-[#FFFFFF] border-slate-200 text-slate-900 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Chargeback</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-4" data-testid="cb-detail">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Field label="ID" value={<span className="font-mono">{active.chargeback_id}</span>} />
                <Field label="Transaction" value={<span className="font-mono">{active.transaction_id}</span>} />
                <Field label="Reason" value={active.reason} />
                <Field label="Disputed" value={`$${Number(active.amount_disputed).toFixed(2)}`} />
                <Field label="Status" value={<StatusPill status={active.status} />} />
                <Field label="Guarantee" value={active.guarantee_eligible ? "Eligible" : "Not eligible"} />
              </div>
              {active.customer_claim && (
                <div className="p-3 rounded bg-slate-50 border border-slate-200 text-sm text-slate-700">
                  <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Customer claim</div>
                  {active.customer_claim}
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Evidence</div>
                <Textarea value={evidence} onChange={(e) => setEvidence(e.target.value)}
                  className="bg-[#F8FAFC] border-slate-200 min-h-[100px]"
                  placeholder="Attach narrative, tracking, signatures, IP logs..."
                  data-testid="cb-evidence-input" />
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button variant="ghost" className="border border-slate-300" onClick={() => update("evidence_submitted")} data-testid="cb-submit-evidence-btn">Submit Evidence</Button>
                <Button className="bg-[#10B981] hover:bg-[#10C99A] text-black" onClick={() => update("won")} data-testid="cb-mark-won">Mark Won</Button>
                <Button variant="ghost" className="border border-[#FF4757]/30 text-[#FF4757] hover:bg-[#FF4757]/10" onClick={() => update("lost")} data-testid="cb-mark-lost">Mark Lost</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 text-slate-800">{value}</div>
    </div>
  );
}
